import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  parseAlertDate,
  isAlertRead,
  isAlertDismissed,
} from '../lib/userAlerts';

function severityColor(sev) {
  if (sev === 'critical') return 'error';
  if (sev === 'warning') return 'warning';
  if (sev === 'success') return 'success';
  return 'info';
}

export default function AlertDetailView({
  item,
  uid,
  navIndex,
  navTotal,
  hasPrev,
  hasNext,
  onBack,
  onPrev,
  onNext,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onRestore,
  onDeletePermanent,
  canHardDelete,
  onCreateTicket,
  canCreateTicket,
  onOpenLinkedTicket,
  canOpenTicket,
}) {
  if (!item) return null;

  const read = isAlertRead(item, uid);
  const dismissed = isAlertDismissed(item, uid);
  const created = parseAlertDate(item.createdAt);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderLeftWidth: 4, borderLeftColor: 'primary.main' }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
          <Button startIcon={<ArrowBackIcon />} size="small" onClick={onBack}>
            Back to list
          </Button>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              disabled={!hasPrev}
              onClick={onPrev}
            >
              Previous
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, alignSelf: 'center' }}>
              {navTotal > 0 ? `${navIndex + 1} / ${navTotal}` : '—'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ChevronRightIcon />}
              disabled={!hasNext}
              onClick={onNext}
            >
              Next
            </Button>
          </Stack>
        </Stack>

        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              {item.title || 'Notification'}
            </Typography>
            <Chip size="small" label={item.severity || 'info'} color={severityColor(item.severity)} />
            {!read && <Chip size="small" label="Unread" color="warning" variant="outlined" />}
            {dismissed && (
              <Chip size="small" label="Archived" variant="outlined" icon={<ArchiveOutlinedIcon />} />
            )}
          </Stack>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {item.message || 'No message provided.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {created ? created.toLocaleString() : '—'}
            {' · '}
            From {item.createdByEmail || 'Super Admin'}
            {item.ruleCode ? ` · ${item.ruleCode}` : ''}
          </Typography>
        </Box>

        <Divider />

        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
          {uid && !read && (
            <Button size="small" variant="outlined" startIcon={<MarkEmailReadIcon />} onClick={() => onMarkRead(item.id)}>
              Mark as read
            </Button>
          )}
          {uid && read && (
            <Button size="small" variant="outlined" startIcon={<MarkEmailUnreadIcon />} onClick={() => onMarkUnread(item.id)}>
              Mark as unread
            </Button>
          )}
          {uid && !dismissed && (
            <Button size="small" color="warning" variant="outlined" startIcon={<ArchiveOutlinedIcon />} onClick={() => onArchive(item.id)}>
              Remove from inbox (archive)
            </Button>
          )}
          {uid && dismissed && (
            <Button size="small" variant="outlined" startIcon={<UnarchiveIcon />} onClick={() => onRestore(item.id)}>
              Restore to inbox
            </Button>
          )}
          {canHardDelete && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<DeleteForeverIcon />}
              onClick={() => onDeletePermanent(item)}
            >
              Delete permanently
            </Button>
          )}
          {canCreateTicket && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<ConfirmationNumberIcon />}
              onClick={() => onCreateTicket(item)}
            >
              Create ticket from notification
            </Button>
          )}
          {canOpenTicket && (
            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => onOpenLinkedTicket(item)}>
              Open linked ticket
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
